import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { decisionPayload, decisionProblem, emptyDecisionForm, emptyOption, governanceError, newKey } from "../lib/governance";
import { InterestedField, LinkList, LinkPicker, PeopleMultiSelect, useGovernancePeople } from "../components/governance/GovernanceBits";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";

function Section({ title, hint, children }) {
  return (
    <Card className="p-4 sm:p-5 space-y-3">
      <div><h2 className="text-base font-semibold text-slate-900">{title}</h2>{hint && <p className="text-sm text-slate-500">{hint}</p>}</div>
      {children}
    </Card>
  );
}

// Proposing a decision, editing a proposal, or proposing the one that replaces an adopted decision (?remplace=<id>).
export default function GovernanceDecisionForm() {
  const { decisionId } = useParams();
  const [params] = useSearchParams();
  const replaceId = params.get("remplace");
  const meetingId = params.get("reunion");
  const editing = !!decisionId;
  const navigate = useNavigate();
  const people = useGovernancePeople();
  const [form, setForm] = useState(() => emptyDecisionForm());
  const [source, setSource] = useState(null); // the decision being edited
  const [replaced, setReplaced] = useState(null); // the adopted decision this one will replace
  const [meeting, setMeeting] = useState(null); // the meeting it comes out of
  const [phase, setPhase] = useState(editing || replaceId ? "loading" : "ready");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [key, setKey] = useState(newKey);
  const locked = !!source?.locked;

  useEffect(() => {
    const id = decisionId || replaceId;
    if (!id) return undefined;
    let alive = true;
    api.get(`/governance/decisions/${id}`).then(({ data }) => {
      if (!alive) return;
      if (editing) { setSource(data); setForm(emptyDecisionForm(data)); } else {
        setReplaced(data);
        setForm({ ...emptyDecisionForm(), title: data.title, context: `Remplace ${data.number}.\n`, deciders: data.deciders.map((d) => d.id), restricted: !!data.restricted });
      }
      setPhase("ready");
    }).catch(() => { if (alive) setPhase("error"); });
    return () => { alive = false; };
  }, [decisionId, replaceId, editing]);

  useEffect(() => {
    if (!meetingId || editing) return undefined;
    let alive = true;
    api.get(`/governance/meetings/${meetingId}`).then(({ data }) => {
      if (!alive) return;
      setMeeting(data);
      setForm((f) => ({ ...f, restricted: !!data.restricted }));
    }).catch(() => {});
    return () => { alive = false; };
  }, [meetingId, editing]);

  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setKey(newKey()); };
  const problem = useMemo(() => decisionProblem(form), [form]);
  const setOption = (index, patch) => set({ options: form.options.map((o, i) => (i === index ? { ...o, ...patch } : o)) });

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const body = decisionPayload(form);
      let data;
      if (editing) {
        const sent = locked ? { interested: body.interested, links: body.links, restricted: body.restricted, deciders: body.deciders } : body;
        ({ data } = await api.patch(`/governance/decisions/${decisionId}`, { ...sent, expected_version: source.version }));
        toast.success("Décision modifiée");
      } else {
        ({ data } = await api.post("/governance/decisions", { ...body, ...(replaceId ? { supersedes_id: replaceId } : {}), ...(meeting ? { meeting_id: meeting.id } : {}) }, { headers: { "Idempotency-Key": key } }));
        toast.success("Décision proposée");
      }
      navigate(`/gouvernance/decisions/${data.id}`);
    } catch (e) {
      setError(governanceError(e, "Enregistrement impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };

  if (phase === "loading") return <div className="space-y-3"><Skeleton className="h-8 w-64" /><Skeleton className="h-48 w-full" /></div>;
  if (phase === "error") {
    return (
      <Card className="p-6 text-center space-y-2" data-testid="decision-form-error">
        <p className="text-sm text-slate-700">Cette décision est introuvable ou vous n'y avez pas accès.</p>
        <Button asChild variant="outline"><Link to="/gouvernance/decisions">Retour aux décisions</Link></Button>
      </Card>
    );
  }
  const back = editing ? `/gouvernance/decisions/${decisionId}` : "/gouvernance/decisions";
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="decision-form">
      <div>
        <Link to={back} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft size={14} />Retour</Link>
        <h1 className="font-display font-bold text-2xl text-slate-900 mt-1">{editing ? `Modifier ${source.number}` : replaced ? `Remplacer ${replaced.number}` : "Nouvelle décision"}</h1>
        <p className="text-sm text-slate-500">{replaced ? "La nouvelle décision cite l'ancienne. Celle-ci reste en vigueur tant que la nouvelle n'est pas adoptée." : "Posez la question, les options examinées et ceux qui tranchent. La décision est adoptée ensuite, par quelqu'un d'autre que vous."}</p>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_20rem] items-start">
        <div className="space-y-4 min-w-0">
          {locked && <p className="rounded-md bg-amber-50 text-amber-900 text-sm px-3 py-2" data-testid="decision-locked-note">Cette décision est figée : son contenu ne se modifie plus. Vous pouvez encore ajuster les personnes concernées et les liens.</p>}
          <Section title="La question">
            <div className="space-y-1.5"><Label htmlFor="dec-title">Titre</Label>
              <Input id="dec-title" value={form.title} maxLength={200} onChange={(e) => set({ title: e.target.value })} className="h-10" disabled={locked} data-testid="decision-title" /></div>
            <div className="space-y-1.5"><Label htmlFor="dec-context">Contexte</Label>
              <Textarea id="dec-context" rows={5} maxLength={4000} value={form.context} onChange={(e) => set({ context: e.target.value })} disabled={locked} data-testid="decision-context"
                placeholder="Pourquoi faut-il décider ? Que sait-on déjà ?" /></div>
          </Section>
          <Section title="Options examinées" hint="Facultatif. Une ligne par possibilité, avec ses avantages et ses inconvénients.">
            <div className="space-y-3">
              {form.options.map((o, i) => (
                <div key={o.id || i} className="rounded-md border border-slate-200 p-3 space-y-2" data-testid={`option-${i}`}>
                  <div className="flex gap-2">
                    <Input value={o.label} maxLength={200} onChange={(e) => setOption(i, { label: e.target.value })} placeholder={`Option ${i + 1}`} className="h-10" disabled={locked} aria-label={`Option ${i + 1}`} data-testid={`option-label-${i}`} />
                    {!locked && <Button type="button" variant="ghost" className="h-10 w-10 p-0 shrink-0" onClick={() => set({ options: form.options.filter((_, j) => j !== i) })} aria-label={`Retirer l'option ${i + 1}`}><Trash2 size={15} /></Button>}
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Textarea rows={2} maxLength={500} value={o.pros} onChange={(e) => setOption(i, { pros: e.target.value })} placeholder="Pour" disabled={locked} aria-label={`Avantages de l'option ${i + 1}`} />
                    <Textarea rows={2} maxLength={500} value={o.cons} onChange={(e) => setOption(i, { cons: e.target.value })} placeholder="Contre" disabled={locked} aria-label={`Inconvénients de l'option ${i + 1}`} />
                  </div>
                </div>
              ))}
              {!locked && form.options.length < 8 && <Button type="button" variant="outline" size="sm" onClick={() => set({ options: [...form.options, emptyOption()] })} data-testid="option-add"><Plus size={14} className="mr-1.5" />Ajouter une option</Button>}
            </div>
          </Section>
          <Section title="Qui" hint="Les décideurs reçoivent la demande et tranchent. Les personnes concernées sont seulement nommées : elles ne décident pas.">
            <div className="space-y-1.5"><Label>Décideurs</Label><PeopleMultiSelect value={form.deciders} onChange={(deciders) => set({ deciders })} people={people} placeholder="Choisir les décideurs" testid="decision-deciders" /></div>
            <div className="space-y-1.5"><Label>Personnes concernées</Label><InterestedField value={form.interested} onChange={(interested) => set({ interested })} people={people} /></div>
          </Section>
          <Section title="Liens" hint="Rattachez la décision à ce qu'elle concerne. Un lien ne modifie rien dans l'autre module : ni montant, ni statut.">
            <LinkList links={form.links} openable={false} onRemove={(l) => set({ links: form.links.filter((x) => x !== l) })} />
            <LinkPicker value={form.links} onChange={(links) => set({ links })} />
          </Section>
        </div>
        <div className="space-y-4 lg:sticky lg:top-4">
          <Card className="p-4 space-y-3">
            <label className="flex items-start justify-between gap-3 cursor-pointer">
              <span><span className="block text-sm font-medium text-slate-900">Décision restreinte</span><span className="block text-xs text-slate-500">Visible seulement de son auteur, des décideurs nommés et des personnes qui voient tout.</span></span>
              <Switch checked={form.restricted} onCheckedChange={(restricted) => set({ restricted })} aria-label="Décision restreinte" data-testid="decision-restricted-switch" />
            </label>
          </Card>
          {meeting && (
            <Card className="p-4 space-y-1" data-testid="decision-from-meeting">
              <h3 className="text-sm font-semibold text-slate-900">Issue de la réunion</h3>
              <p className="text-sm text-slate-700">{meeting.number} — {meeting.title}</p>
            </Card>
          )}
          {replaced && (
            <Card className="p-4 space-y-1" data-testid="decision-replaces">
              <h3 className="text-sm font-semibold text-slate-900">Décision remplacée</h3>
              <p className="text-sm text-slate-700">{replaced.number} — {replaced.title}</p>
              {replaced.decision && <p className="text-xs text-slate-500 whitespace-pre-wrap">{replaced.decision}</p>}
            </Card>
          )}
          <Card className="p-4 space-y-2 text-sm text-slate-600">
            <h3 className="font-semibold text-slate-900">Ce qui se passe ensuite</h3>
            <p>Une décision commence « proposée ». Un décideur peut l'adopter, la différer avec une date de revue, ou l'annuler.</p>
            <p>Adopter une décision n'exécute rien : les actions à mener sont des tâches que vous créez depuis sa fiche.</p>
          </Card>
        </div>
      </div>
      {error && <p className="text-sm text-red-600" role="alert" data-testid="decision-form-submit-error">{error}</p>}
      <div className="flex flex-wrap gap-2 justify-end">
        <Button asChild variant="outline" type="button"><Link to={back}>Annuler</Link></Button>
        <Button type="submit" disabled={busy || (!locked && !!problem)} title={(!locked && problem) || undefined} data-testid="decision-submit">
          {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{editing ? "Enregistrer" : "Proposer la décision"}
        </Button>
      </div>
    </form>
  );
}
